// Event contract v1. Identical in web, Expo, and backend; checked by contract tests.
// Dates are event-local calendar dates, not instants. Mongo stores them at UTC midnight.
const DATE = /^\d{4}-\d{2}-\d{2}$/;

function calendarDate(value) {
  if (!value) return '';
  const raw = value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
  if (!DATE.test(raw)) throw new Error('Use a date in YYYY-MM-DD format');
  const parsed = new Date(raw + 'T00:00:00.000Z');
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== raw) {
    throw new Error('Invalid calendar date');
  }
  return raw;
}

function clockTime(value) {
  if (value == null || value === '') return '';
  const match = String(value).trim().match(/^(\d{1,2}):(\d{1,2})(?:\s*([AP]M))?$/i);
  if (!match) throw new Error('Use a time in HH:mm format');
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3]?.toUpperCase();
  if (minute > 59 || hour > 23 || (period && (hour < 1 || hour > 12))) throw new Error('Invalid event time');
  if (period) hour = hour % 12 + (period === 'PM' ? 12 : 0);
  return String(hour).padStart(2, '0') + ':' + String(minute).padStart(2, '0');
}

function validTimeZone(value) {
  if (!value || typeof value !== 'string') return false;
  try { new Intl.DateTimeFormat('en-US', { timeZone: value }).format(); return true; }
  catch { return false; }
}

function coordinate(value, maximum) {
  if (value == null || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && Math.abs(number) <= maximum ? number : null;
}

/** @param {any} input */
function normalizeEvent(input) {
  const startDate = calendarDate(input.startDate);
  const endDate = calendarDate(input.endDate);
  const startTime = clockTime(input.startTime);
  const endTime = clockTime(input.endTime);
  const timeZone = String(input.timeZone || '').trim();
  if (!startDate) throw new Error('Select an event date');
  if (!validTimeZone(timeZone)) throw new Error('Select a valid event time zone');
  if (endTime && !endDate) throw new Error('Select an end date for the end time');
  if (endDate && (endDate < startDate || (endDate === startDate && startTime && endTime && endTime < startTime))) {
    throw new Error('The event must end after it starts');
  }
  const eventFormat = input.eventFormat || 'physical';
  if (!['physical', 'online'].includes(eventFormat)) throw new Error('Invalid event format');
  let locationAddress = String(input.locationAddress || '').trim();
  let onlineUrl = String(input.onlineUrl || '').trim();
  let lat = coordinate(input.coordinatesLat ?? input.coordinates?.lat, 90);
  let lng = coordinate(input.coordinatesLng ?? input.coordinates?.lng, 180);
  if (eventFormat === 'online') {
    try {
      const url = new URL(onlineUrl);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error();
    } catch { throw new Error('Add a valid online event link'); }
    locationAddress = 'Online';
    lat = null;
    lng = null;
  } else {
    if (!locationAddress || lat === null || lng === null) throw new Error('Confirm a valid physical event location');
    onlineUrl = '';
  }
  return { startDate, endDate: endDate || null, startTime, endTime, timeZone,
    eventFormat, locationAddress, onlineUrl, coordinates: { lat, lng } };
}

function dateInZone(now, timeZone) {
  if (!validTimeZone(timeZone)) throw new Error('Invalid search time zone');
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const part = (type) => parts.find((item) => item.type === type).value;
  return part('year') + '-' + part('month') + '-' + part('day');
}

function addDays(date, days) {
  const result = new Date(calendarDate(date) + 'T00:00:00Z');
  result.setUTCDate(result.getUTCDate() + days);
  return result.toISOString().slice(0, 10);
}

// Search dates refer to the displayed event-local dates. "Today" uses the viewer's zone.
function searchDateRange(filter = 'any', timeZone = 'UTC', now = new Date()) {
  const today = dateInZone(now, timeZone);
  if (filter === 'any') return { from: today, to: null };
  if (filter === 'today') return { from: today, to: today };
  if (filter === 'tomorrow') return { from: addDays(today, 1), to: addDays(today, 1) };
  if (filter === 'week') return { from: today, to: addDays(today, 6) };
  if (filter === 'weekend') {
    const weekday = new Date(today + 'T00:00:00Z').getUTCDay();
    const from = weekday === 0 ? today : addDays(today, (6 - weekday + 7) % 7);
    return { from, to: weekday === 0 ? today : addDays(from, 1) };
  }
  throw new Error('Invalid date filter');
}

function formatEventDate(value, locale = 'en-US', fallback = 'Date to be announced') {
  if (!value) return fallback;
  const date = calendarDate(value);
  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric' })
    .format(new Date(date + 'T12:00:00Z'));
}

function formatEventTime(value, locale = 'en-US') {
  const time = clockTime(value);
  if (!time) return '';
  const [hour, minute] = time.split(':').map(Number);
  const date = new Date(Date.UTC(2000, 0, 1, hour, minute));
  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' }).format(date);
}

/** @param {any} schedule */
function formatEventSchedule(schedule = {}, locale = 'en-US', fallback) {
  const time = formatEventTime(schedule.startTime, locale);
  return formatEventDate(schedule.startDate, locale, fallback) + (time ? ' · ' + time : '')
    + (schedule.timeZone ? ' · ' + schedule.timeZone.replace(/_/g, ' ') : '');
}

module.exports = { calendarDate, clockTime, validTimeZone, coordinate, normalizeEvent,
  dateInZone, addDays, searchDateRange, formatEventDate, formatEventTime, formatEventSchedule };
